import React from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import Icon from '../../icon'
import styles from './BusinessProfileLeftSidebar.module.scss'

/**
 * Business profile left sidebar – first block: name + handshake icon, divider,
 * then location, industry, bio, website, divider, followers, divider, causes (each separated by a line).
 */
export default function BusinessProfileLeftSidebar({
  name,
  location,
  industryName,
  bio,
  websiteUrl,
  followersCount = 0,
  categories = [],
  locale = 'en',
}) {
  const { t } = useTranslation()

  const displayLocation = location || ''
  const displayIndustry = industryName || ''
  const displayBio = bio || ''
  const displayWebsite = websiteUrl ? (websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`) : ''
  const causeList = Array.isArray(categories) ? categories : []

  return (
    <div className={styles.sidebar}>
      <section className={styles.section}>
        <div className={styles.nameRow}>
          {name != null && <h2 className={styles.name}>{name}</h2>}
          <Icon name="handshake" className={styles.nameIcon} aria-hidden />
        </div>

        <hr className={styles.divider} />

        {displayLocation ? (
          <>
            <div className={styles.labelRow}>
              <span className={styles.label}>{t('profiles.business.location', 'Location')}</span>
            </div>
            <p className={styles.value}>{displayLocation}</p>
          </>
        ) : null}

        {displayIndustry ? (
          <>
            <div className={styles.labelRow}>
              <span className={styles.label}>{t('profiles.business.industry', 'Industry')}</span>
            </div>
            <p className={styles.value}>{displayIndustry}</p>
          </>
        ) : null}

        {displayBio ? (
          <p className={styles.bio}>{displayBio}</p>
        ) : null}

        {displayWebsite ? (
          <p className={styles.websiteRow}>
            <a
              href={displayWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.websiteLink}
            >
              {websiteUrl.replace(/^https?:\/\//i, '')}
            </a>
          </p>
        ) : null}

        <hr className={styles.divider} />

        <div className={styles.followersRow}>
          <Icon name="user-plus" className={styles.followersIcon} aria-hidden />
          <span className={styles.followersText}>
            <span className={styles.followersCount}>{followersCount}</span>{' '}
            {t('profiles.followers', 'Followers')}
          </span>
        </div>

        <hr className={styles.divider} />

        {causeList.length > 0 ? (
          <ul className={styles.causeList}>
            {causeList.map((cause, i) => (
              <li key={cause.id ?? i} className={styles.causeItem}>
                <Icon name={cause.icon || 'heart'} className={styles.causeIcon} aria-hidden />
                <span className={styles.causeName}>
                  {locale === 'es' ? (cause.es_name ?? cause.en_name) : (cause.en_name ?? cause.es_name)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.placeholder}>{t('profiles.business.noCauses', 'No causes selected.')}</p>
        )}
      </section>
    </div>
  )
}

BusinessProfileLeftSidebar.propTypes = {
  name: PropTypes.string,
  location: PropTypes.string,
  industryName: PropTypes.string,
  bio: PropTypes.string,
  websiteUrl: PropTypes.string,
  followersCount: PropTypes.number,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      en_name: PropTypes.string,
      es_name: PropTypes.string,
      icon: PropTypes.string,
    })
  ),
  locale: PropTypes.string,
}
